import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { 
  Plus,
  Download,
  ArrowLeft,
  ChevronRight,
  Home,
  User,
  Mail,
  Phone,
  Shield,
  Trash2,
  Users,
  Building,
  CheckCircle
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import userService from '../../services/userService'
// Consolidated CSS is imported globally'

const UserMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    roles: 0
  })
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    user: null
  })

  // Load users data
  useEffect(() => {
    loadData()
  }, [])

  // Optimized data loading with parallel requests
  const loadData = useCallback(async () => {
    try {
      // Check frontend cache first
      const cacheKey = 'userMasterData'
      const cachedData = localStorage.getItem(cacheKey)
      const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`)
      const cacheExpiry = 30000 // 30 seconds
      
      // If we have recent cached data, use it immediately
      if (cachedData && cacheTimestamp && (Date.now() - parseInt(cacheTimestamp)) < cacheExpiry) {
        const parsedData = JSON.parse(cachedData)
        setData(parsedData.users || [])
        setStats(parsedData.stats || {
          totalUsers: 0,
          activeUsers: 0,
          inactiveUsers: 0,
          roles: 0
        })
        setLoading(false)
        setStatsLoading(false)
        return
      }
      
      setLoading(true)
      setStatsLoading(true)
      
      // Load users and stats in parallel for better performance
      // Request larger limit for small datasets to trigger fast path
      const [usersResponse, statsResponse] = await Promise.allSettled([
        userService.getAllUsers({ limit: 100 }), // Request larger limit for fast path
        userService.getUserStats()
      ])

      // Handle users data
      if (usersResponse.status === 'fulfilled' && usersResponse.value.success) {
        const transformedData = userService.transformUsersForDisplay(usersResponse.value.data)
        setData(transformedData)
      } else {
        console.error('Error loading users:', usersResponse.reason)
        toast.error('Failed to load users')
      }

      // Handle stats data
      if (statsResponse.status === 'fulfilled' && statsResponse.value.success) {
        setStats({
          totalUsers: statsResponse.value.data.total,
          activeUsers: statsResponse.value.data.active,
          inactiveUsers: statsResponse.value.data.inactive,
          roles: statsResponse.value.data.byType?.length || 0
        })
      } else {
        console.error('Error loading stats:', statsResponse.reason)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load data')
    } finally {
      setLoading(false)
      setStatsLoading(false)
      
      // Cache the data for next time
      try {
        const dataToCache = {
          users: data,
          stats: stats
        }
        localStorage.setItem(cacheKey, JSON.stringify(dataToCache))
        localStorage.setItem(`${cacheKey}_timestamp`, Date.now().toString())
      } catch (error) {
        console.warn('Failed to cache data:', error)
      }
    }
  }, [])

  // Memoized load users function for refresh operations
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true)
      const response = await userService.getAllUsers({ limit: 100 }) // Use fast path
      if (response.success) {
        const transformedData = userService.transformUsersForDisplay(response.data)
        setData(transformedData)
      }
    } catch (error) {
      console.error('Error loading users:', error)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [])

  // Memoized load stats function for refresh operations
  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true)
      const response = await userService.getUserStats()
      if (response.success) {
        setStats({
          totalUsers: response.data.total,
          activeUsers: response.data.active,
          inactiveUsers: response.data.inactive,
          roles: response.data.byType?.length || 0
        })
      }
    } catch (error) {
      console.error('Error loading stats:', error)
    } finally {
      setStatsLoading(false)
    }
  }, [])

  // Memoized columns to prevent unnecessary re-renders
  const columns = useMemo(() => [
    {
      accessorKey: 'userCode',
      header: 'User Code',
      size: 120,
      cell: ({ getValue }) => (
        <div className="user-code-cell">
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Full Name',
      size: 180,
      cell: ({ getValue }) => (
        <div className="name-cell">
          <span className="name-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: 'Email Address',
      size: 200,
      cell: ({ getValue }) => (
        <div className="email-cell">
          <span className="email-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'role',
      header: 'Role',
      size: 120,
      cell: ({ getValue }) => {
        const role = getValue()
        const roleName = role ? (typeof role === 'object' ? role.roleName : role) : 'No Role'
        return (
          <div className="role-cell">
            <span className="role-text">{roleName}</span>
          </div>
        )
      },
    },
    {
      accessorKey: 'departmentName',
      header: 'Department',
      size: 140,
      cell: ({ getValue }) => (
        <div className="department-cell">
          <span className="department-text">{getValue() || 'No Department'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'designation',
      header: 'Designation',
      size: 140,
      cell: ({ getValue }) => (
        <div className="designation-cell">
          <span className="designation-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 100,
      cell: ({ getValue }) => (
        <div className="status-cell">
          <span className={`status-badge ${getValue().toLowerCase()}`}>
            {getValue()}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'lastLogin',
      header: 'Last Login',
      size: 120,
      cell: ({ getValue }) => (
        <div className="last-login-cell">
          <span className="last-login-text">{getValue()}</span>
        </div>
      ),
    },
  ], [])

  const handleBack = () => {
    navigate('/settings?tab=masters')
  }

  const handleAddNew = () => {
    navigate('/settings/user-master/form')
  }

  const handleExport = () => {
    // Handle export
    console.log('Export User data')
  }

  const handleEdit = (userData) => {
    navigate(`/settings/user-master/edit/${userData.id}`)
  }

  const handleView = (userData) => {
    navigate(`/settings/user-master/view/${userData.id}`)
  }

  const handleDelete = (userData) => {
    setDeleteModal({
      isOpen: true,
      user: userData
    })
  }

  const confirmDelete = async () => {
    if (!deleteModal.user) return

    try {
      const response = await userService.deleteUser(deleteModal.user.id)
      if (response.success) {
        toast.success('User deleted successfully!', { autoClose: 2000 })
        setDeleteModal({ isOpen: false, user: null })
        loadUsers() // Refresh the data
        loadStats() // Refresh the stats
      } else {
        toast.error(response.message || 'Failed to delete user')
      }
    } catch (error) {
      console.error('Error deleting user:', error)
      toast.error('Failed to delete user: ' + error.message)
    }
  }

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, user: null })
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Settings',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'Masters',
      path: '/settings',
      onClick: () => navigate('/settings')
    },
    {
      label: 'User Master',
      path: '/settings/user-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>User Master</h1>
          <p>Manage user accounts and permissions</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className={`metric-card revenue ${statsLoading ? 'card-loading' : ''}`}>
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>+{stats.totalUsers}</span>
            </div>
          </div>
          {statsLoading ? (
            <LoadingSpinner size="small" text="Loading..." />
          ) : (
            <>
              <div className="metric-value">{stats.totalUsers}</div>
              <div className="metric-title">Total Users</div>
              <div className="metric-subtitle">All registered users</div>
            </>
          )}
        </div>

        <div className={`metric-card customers ${statsLoading ? 'card-loading' : ''}`}>
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>+{stats.activeUsers}</span>
            </div>
          </div>
          {statsLoading ? (
            <LoadingSpinner size="small" text="Loading..." />
          ) : (
            <>
              <div className="metric-value">{stats.activeUsers}</div>
              <div className="metric-title">Active Users</div>
              <div className="metric-subtitle">Currently active</div>
            </>
          )}
        </div>

        <div className={`metric-card churn ${statsLoading ? 'card-loading' : ''}`}>
          <div className="metric-header">
            <div className="metric-icon">
              <User size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>{stats.inactiveUsers}</span>
            </div>
          </div>
          {statsLoading ? (
            <LoadingSpinner size="small" text="Loading..." />
          ) : (
            <>
              <div className="metric-value">{stats.inactiveUsers}</div>
              <div className="metric-title">Inactive Users</div>
              <div className="metric-subtitle">Disabled or suspended</div>
            </>
          )}
        </div>

        <div className={`metric-card burn ${statsLoading ? 'card-loading' : ''}`}>
          <div className="metric-header">
            <div className="metric-icon">
              <Shield size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>{stats.roles}</span>
            </div>
          </div>
          {statsLoading ? (
            <LoadingSpinner size="small" text="Loading..." />
          ) : (
            <>
              <div className="metric-value">{stats.roles}</div>
              <div className="metric-title">Roles</div>
              <div className="metric-subtitle">Different user roles</div>
            </>
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className={`${loading ? 'table-loading' : ''}`}>
        <DataTable
          data={data}
          columns={columns}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          enableSelection={true}
          enablePagination={true}
          pageSize={10}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          noCard={true}
          className="sac-master-table"
          loading={loading}
        />
        {loading && (
          <div className="table-loading-overlay">
            <LoadingSpinner size="medium" text="Loading users..." />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <div className="modal-icon">
                <Trash2 size={24} className="text-red-500" />
              </div>
              <h3 className="modal-title">Delete User</h3>
            </div>
            <div className="modal-body">
              <p className="modal-message">
                Are you sure you want to delete <strong>{deleteModal.user?.name}</strong>?
              </p>
              <p className="modal-warning">
                This action cannot be undone. The user will be permanently removed from the system.
              </p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger" 
                onClick={confirmDelete}
              >
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserMaster
